import { ShopItemAdapter } from "../adapters/types";
import { z } from "zod";

import { ItemType } from "@/types/enums/item-type";
import {
  RegularCatalogItemType,
  RegularCatalogPurchaseLineInput,
  RegularCatalogShopCartLine,
} from "@/types/shop";

const regularItemTypes = [
  ItemType.SERVICE,
  ItemType.PRODUCT,
  ItemType.MEDICATION,
] as const;

const lineSchema = z.object({
  kind: z.literal("catalog-item"),
  itemType: z.enum(regularItemTypes),
  quantity: z
    .number()
    .int()
    .min(1, "Quantity must be at least 1.")
    .max(100_000, "Maximum 100,000 items per line."),
  unitPriceCents: z.number().int().positive("Item price is invalid."),
  expectedUnitPriceCents: z.number().int().positive().optional(),
  itemId: z.string().min(1, "Item is invalid."),
});

const validate = (line: RegularCatalogShopCartLine): string | null => {
  const result = lineSchema.safeParse(line);
  if (result.success) return null;
  return result.error.issues[0]?.message ?? "Item line is invalid.";
};

const serializeForCheckout = (
  line: RegularCatalogShopCartLine,
): RegularCatalogPurchaseLineInput => ({
  itemType: line.itemType,
  itemId: line.itemId,
  quantity: line.quantity,
  expectedUnitPriceCents: line.expectedUnitPriceCents ?? line.unitPriceCents,
});

const normalizeLine = (
  line: RegularCatalogShopCartLine,
): RegularCatalogShopCartLine => ({
  ...line,
  quantity: Math.min(100_000, Math.max(1, Math.trunc(line.quantity))),
});

export const createRegularCatalogAdapter = (
  type: RegularCatalogItemType,
): ShopItemAdapter<
  RegularCatalogShopCartLine,
  RegularCatalogPurchaseLineInput
> => ({
  type,
  validate,
  serializeForCheckout,
  normalizeLine,
});
